import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Input, Button, Switch } from 'antd';
import DetailModal from 'components/Basic/DetailModal';
import { ArrowForward, Business } from '@material-ui/icons';
import toast from 'components/Basic/Toast';
import { Img } from 'react-image';
import { getImageLink } from 'utilities/common';
import {
  useCreateSponsorsMutation,
  useUpdateSponsorsMutation,
} from 'core/worldCup/sponsors/SponsorsService';
import { Sponsors } from 'core/worldCup/sponsors/types';
import { sanitizeRule } from 'utilities/sanitize';
import { VALID_IMAGE_FORMATS } from 'utilities/constants';
import { validateImageBytes } from 'utilities/validateImageFile';
import { getApiErrorMessage } from 'utilities/getApiErrorMessages';

type SponsorFormData = {
  name: string;
  logo: string;
  redeemAllEstablishments: boolean;
  isActive: boolean;
};

type SponsorsFormModalType = {
  mode: 'modal' | 'fullscreen';
  isOpenModal: boolean;
  sponsor: Sponsors | null;
  onCloseModal: () => void;
  onSaveSuccess: () => void;
};

const SponsorsFormModal: React.FC<SponsorsFormModalType> = ({
  mode,
  isOpenModal,
  sponsor,
  onCloseModal,
  onSaveSuccess,
}) => {
  const [form] = Form.useForm<SponsorFormData>();
  const intl = useIntl();
  const fileRef = useRef<HTMLInputElement>(null);
  const isModal = mode === 'modal';

  const [fileSrc, setFileSrc] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [dimensions, setDimensions] = useState({ type: '', width: 0, height: 0 });

  const [createSponsor, { isLoading: isCreating }] = useCreateSponsorsMutation();
  const [updateSponsor, { isLoading: isUpdating }] = useUpdateSponsorsMutation();

  useEffect(() => {
    if (isOpenModal) {
      if (sponsor) {
        form.setFieldsValue({
          name: sponsor.name || '',
          redeemAllEstablishments: sponsor.redeemAllEstablishments,
          isActive: sponsor.isActive,
          logo: sponsor.logo,
        });
        setFileSrc(getImageLink(sponsor.logo));
        setLogoFile(null);
        setDimensions({ type: '', width: 0, height: 0 });
      } else {
        form.resetFields();
        setFileSrc(null);
        setLogoFile(null);
        setDimensions({ type: '', width: 0, height: 0 });
      }
    }
  }, [isOpenModal, sponsor, form]);

  const openChooseFile = () => {
    fileRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error({ title: intl.formatMessage({ id: 'Image size should be less than 2MB' }) });
      return;
    }
    if (!VALID_IMAGE_FORMATS.includes(file.type)) {
      toast.error({ title: intl.formatMessage({ id: 'Incorrect file type' }) });
      return;
    }
    const isValidImage = await validateImageBytes(file);
    if (!isValidImage) {
      toast.error({ title: intl.formatMessage({ id: 'Incorrect file type' }) });
      return;
    }

    setLogoFile(file);
    form.setFieldsValue({ logo: file.name });

    const reader = new FileReader();
    reader.onload = () => {
        const img = new Image();
        img.onload = () => {
            setDimensions({
                width: img.width,
                height: img.height,
                type: file.type,
            });
        };
        const result = reader.result as string;
        img.src = result;
        setFileSrc(result);
        form.validateFields(['logo']);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handleFinish = async (values: SponsorFormData) => {
    const formData = new FormData();
    formData.append('name', values.name.trim());
    formData.append('redeemAllEstablishments', 'true');
    if (logoFile) {
      formData.append('logo', logoFile);
    }

    try {
      if (sponsor && sponsor.id) {
        formData.append('isActive', values.isActive ? 'true' : 'false');
        if(!logoFile) {
          formData.append('logo', sponsor.logo ? sponsor.logo : '');
        }
        await updateSponsor({ id: sponsor.id, data: formData }).unwrap();
        toast.success({ title: intl.formatMessage({ id: 'Sponsor is updated successfully!' }) });
      } else {
        formData.append('isActive', 'true');
        await createSponsor(formData).unwrap();
        toast.success({ title: intl.formatMessage({ id: 'Sponsor is created successfully!' }) });
      }

      onCloseModal();
      onSaveSuccess();

    } catch (e: any) {
      console.error('Sponsor save error:', e);
      const defaultMsg = intl.formatMessage({ id: sponsor?.id ? 'Updating sponsor is failure!' : 'Creating sponsor is failure!' });
      toast.error({ title: getApiErrorMessage(e, intl.locale, defaultMsg) });
    }
  };

  const isEditMode = sponsor && sponsor.id;

  const renderFormContent = () => (
    <Form
      form={form}
      className={isModal ? 'basic_info_form' : 'sponsors_form'}
      onFinish={handleFinish}
      initialValues={{ name: '', logo: null, isActive: true }}
    >
      {isModal ? (
        <>
          <div className="sponsors_input">
            <p className="title"><FormattedMessage id="Sponsor name" /></p>
            <Form.Item name="name" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }, sanitizeRule(intl.formatMessage({ id: 'Invalid characters detected' }))]}>
              <Input placeholder={intl.formatMessage({ id: 'Sponsor name' })} maxLength={200} />
            </Form.Item>
          </div>
          <div className="sponsors_input">
            <Form.Item
              name="logo"
              rules={[
                {
                  validator: async (_, value) => {
                    if (!fileSrc && !sponsor?.logo) {
                      return Promise.reject(new Error(intl.formatMessage({ id: 'Image is required' })));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <div className="photo_picker_wrapper">
                <div className="photo_info">
                  <p className="title"><FormattedMessage id="Sponsor image" /></p>
                  {dimensions.type && <p className="description">
                    Archivo {dimensions.type} de {dimensions.width}px por {dimensions.height}px
                  </p>}
                  <Button className="select_photo_btn" onClick={openChooseFile}>
                    <FormattedMessage id="Select File" />
                  </Button>
                </div>
                <div className="photo_area" onClick={openChooseFile}>
                  <Img src={fileSrc || ''} alt="Sponsor image" />
                </div>
                <input type="file" className="file_input" accept="image/*" ref={fileRef} onChange={handleFileChange} hidden />
              </div>
            </Form.Item>
          </div>
          <div className={isEditMode ? 'side_btns' : 'side_btns_camp'}>
            {isEditMode && (
              <div className="aditional_detail">
                <p><FormattedMessage id="Active" /></p>
                <Form.Item name="isActive" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
              </div>
            )}
            <div className="btn_wrapper">
              <Button className="save_sar_btn" htmlType="submit" loading={isCreating || isUpdating}>
                <FormattedMessage id={isEditMode ? 'Save changes' : 'Save'} />
                <ArrowForward />
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="sponsors_form_fields">
            <div className="sponsors_form_row">
              <div className="sponsors_field">
                <p className="title"><FormattedMessage id="Sponsor name" /></p>
                <Form.Item name="name" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }, sanitizeRule(intl.formatMessage({ id: 'Invalid characters detected' }))]}>
                  <Input placeholder={intl.formatMessage({ id: 'Sponsor name' })} maxLength={200} />
                </Form.Item>
              </div>
            </div>
            <div className="sponsors_form_row">
              <Form.Item
                name="logo"
                rules={[
                  {
                    validator: async (_, value) => {
                      if (!fileSrc && !sponsor?.logo) {
                        return Promise.reject(new Error(intl.formatMessage({ id: 'Image is required' })));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <div className="photo_picker_wrapper">
                  <div className="photo_info">
                    <p className="title"><FormattedMessage id="Sponsor image" /></p>
                    {dimensions.type && <p className="description">
                      Archivo {dimensions.type} de {dimensions.width}px por {dimensions.height}px
                    </p>}
                    <Button className="select_photo_btn" onClick={openChooseFile}>
                      <FormattedMessage id="Select File" />
                    </Button>
                  </div>
                  <div className="photo_area" onClick={openChooseFile}>
                    <Img src={fileSrc || ''} alt="Sponsor image" />
                  </div>
                  <input type="file" className="file_input" accept="image/*" ref={fileRef} onChange={handleFileChange} hidden />
                </div>
              </Form.Item>
            </div>
          </div>
          <div className="sponsors_form_actions">
            {isEditMode && (
              <div className="aditional_detail">
                <p><FormattedMessage id="Active" /></p>
                <Form.Item name="isActive" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
              </div>
            )}
            <div className="btn_wrapper">
              <Button className="save_btn" htmlType="submit" loading={isCreating || isUpdating}>
                <FormattedMessage id={isEditMode ? 'Save changes' : 'Save'} />
                <ArrowForward />
              </Button>
              <Button className="cancel_btn" onClick={onCloseModal}>
                <FormattedMessage id="Cancel" />
              </Button>
            </div>
          </div>
        </>
      )}
    </Form>
  );

  if (isModal) {
    return (
      <DetailModal
        className="sponsors_view_modal"
        visible={isOpenModal}
        width={430}
        title={<FormattedMessage id={isEditMode ? 'Edit sponsor' : 'New sponsor'} />}
        onClose={onCloseModal}
        sections={[
          {
            icon: <Business />,
            title: <FormattedMessage id="Sponsor information" />,
            children: (
              <div className="new_sponsors_form_wrapper">
                {renderFormContent()}
              </div>
            ),
          },
        ]}
      />
    );
  }

  if (!isOpenModal) return null;

  return (
    <div className="sponsors_form_fullscreen">
      <div className="sponsors_form_wrapper">
        <div className="sponsors_form_header">
          <h3><FormattedMessage id={isEditMode ? 'Edit sponsor' : 'New sponsor'} /></h3>
        </div>
        {renderFormContent()}
      </div>
    </div>
  );
};

export default SponsorsFormModal;
